import { OAuthStore } from "./oauth-store";
import { OAuthVerifier } from "./oauth-verifier";
import { generatePKCECodeChallenge, generatePKCECodeVerifier } from "./pkce-generator";
export class OAuthService {
    oauthStore;
    logStore;
    static instance;
    // do not try to change isOauthEnabled to inline function like:
    // process.env.OAUTH_ENABLED === "true"
    // This will break the deployment process where BAKED_OAUTH_ENABLED vars are not properly configured.
    static _isOAuthEnabled = process.env.OAUTH_ENABLED ?? "";
    clientId = process.env.OAUTH_CLIENT_ID ?? "";
    scope = process.env.OAUTH_SCOPE ?? "";
    authorizationEndpoint = process.env.OAUTH_AUTHORIZE_URL ?? "";
    redirectUri = process.env.OAUTH_AUTHORIZE_REDIRECT_URL ?? "";
    tokenUrl = process.env.OAUTH_TOKEN_URL ?? "";
    constructor(oauthStore, logStore) {
        this.oauthStore = oauthStore;
        this.logStore = logStore;
    }
    static getInstance(logStore) {
        if (!this.instance) {
            this.instance = new OAuthService(OAuthStore.getInstance(), logStore);
        }
        return this.instance;
    }
    static getInstanceOrThrow() {
        if (!this.instance) {
            throw new Error("OAuthService is undefined");
        }
        return this.instance;
    }
    static get isOAuthEnabled() {
        return this._isOAuthEnabled === "true";
    }
    async getToken() {
        const oauth = OAuthStore.getInstance();
        if (oauth.isAuthenticated()) {
            return Promise.resolve(OAuthStore.getInstance().accessToken ?? "");
        }
        else if (oauth.tokenExpired()) {
            // fetch new token
            const resp = await this.silentRefreshToken();
            if (resp) {
                return Promise.resolve(oauth.accessToken ?? "");
            }
        }
        // log out otherwise
        oauth.clearToken();
        this.logStore.logError({
            appMessage: "OAuth token not found. Please log in again."
        });
        // safe to resolve with empty string, because we notified user about situation in logError
        return Promise.resolve("");
    }
    async prepareOAuthRedirectURLwithPKCE() {
        const codeChallenge = await this.generatePKCEchallenge();
        return this.getOAuthRedirectUri(codeChallenge);
    }
    async exchangeCodeForToken(code) {
        try {
            const token = await this.exchangeCode(code);
            if (token) {
                this.oauthStore.setToken(token.token_type, token.scope, token.expires_in, token.ext_expires_in, token.access_token, token.refresh_token);
            }
            else {
                throw new Error("OAuth exchange code for token response is invalid.");
            }
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            this.logStore.logError({
                appMessage: "OAuth exchange code for token failed. Please log in again.",
                error
            });
            return Promise.reject(err);
        }
    }
    async silentRefreshToken() {
        try {
            const token = await this.refreshToken(this.oauthStore?.refreshToken);
            if (token) {
                this.oauthStore.setToken(token.token_type, token.scope, token.expires_in, token.ext_expires_in, token.access_token, token.refresh_token);
                return token.access_token;
            }
            else {
                throw new Error("OAuth token response is invalid.");
            }
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            this.logStore.logError({
                appMessage: "OAuth token refresh failed. Please log in again.",
                error
            });
            return Promise.reject(err);
        }
    }
    // Private part
    // Private methods only reject promises, try-catch blocks are used in public methods
    // Create redirect URL for OAuth - step 1
    getOAuthRedirectUri(codeChallenge) {
        return `${this.authorizationEndpoint}?response_type=code&client_id=${this.clientId}` +
            `&redirect_uri=${encodeURIComponent(this.redirectUri)}&scope=${encodeURIComponent(this.scope)}` +
            `&code_challenge=${codeChallenge}&code_challenge_method=S256`;
    }
    // Method generates code verifier, stores it into OAuthVerifier
    // also generate PKCE code challenge, that is part of redirect URI
    async generatePKCEchallenge() {
        const codeVerifier = generatePKCECodeVerifier();
        OAuthVerifier.setCodeVerifier(codeVerifier);
        return await generatePKCECodeChallenge(codeVerifier);
    }
    async refreshToken(refreshToken) {
        if (!refreshToken) {
            return Promise.reject(new Error("OAuth refresh token not provided"));
        }
        const params = new URLSearchParams({
            client_id: this.clientId,
            refresh_token: refreshToken,
            grant_type: "refresh_token",
            scope: this.scope
        });
        return await this.callTokenEndpoint(this.tokenUrl, params.toString());
    }
    async exchangeCode(authCode) {
        const codeVerifier = OAuthVerifier.getCodeVerifier();
        if (!codeVerifier) {
            return Promise.reject(new Error("OAuth code verifier not found"));
        }
        const params = new URLSearchParams({
            client_id: this.clientId,
            redirect_uri: this.redirectUri,
            grant_type: "authorization_code",
            code: authCode,
            code_verifier: codeVerifier,
            scope: this.scope
        });
        return await this.callTokenEndpoint(this.tokenUrl, params.toString());
    }
    async callTokenEndpoint(tokenUrl, body) {
        try {
            const response = await fetch(tokenUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: body,
            });
            if (!response.ok) {
                return Promise.reject(new Error(`OAuth: token response error. Status: ${response.statusText} Code: ${response.status}`));
            }
            return await response.json();
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            return Promise.reject(error);
        }
    }
}
